import { Link, useRouterState } from "@tanstack/react-router";
import { Home, Repeat, Images, Settings, Lock, LineChart } from "lucide-react";
import { type ReactNode } from "react";
import { useWallet } from "@/lib/wallet/store";
import logo from "@/assets/cobalt-co-logo.png";

const tabs = [
  { to: "/", label: "Home", Icon: Home },
  { to: "/markets", label: "Markets", Icon: LineChart },
  { to: "/swap", label: "DEX Swap", Icon: Repeat },
  { to: "/nfts", label: "NFTs", Icon: Images },
  { to: "/settings", label: "Settings", Icon: Settings },
] as const;


export function AppShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { unlocked, lock, vault, hydrated } = useWallet();

  // The tab bar only exists once there is an unlocked account to navigate.
  const showTabs = hydrated && Boolean(vault) && unlocked;

  return (
    <div className="flex min-h-screen justify-center bg-background text-foreground">
      <div className="relative flex min-h-screen w-full max-w-[430px] flex-col bg-background">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border/80 px-5">
          <img
            src={logo}
            alt="Cobalt Wallet"
            width={1024}
            height={1024}
            className="size-11 object-contain"
          />
          <div className="leading-none">
            <p className="text-[14px] font-semibold">Cobalt Wallet</p>
            <p className="readout mt-0.5 text-[9px] text-faint">MAINNET · XRPL</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="flex h-6 items-center gap-1.5 rounded-md border border-border bg-surface px-2.5">
              <span className="size-1.5 rounded-full bg-accent" />
              <span className="readout text-[10px] text-muted">KEY·LOCAL</span>
            </span>
            {unlocked ? (
              <button
                type="button"
                onClick={lock}
                aria-label="Lock wallet"
                className="grid size-6 place-items-center rounded-md border border-border bg-surface text-muted transition-colors hover:text-accent"
              >
                <Lock className="size-3" />
              </button>
            ) : null}
          </div>
        </header>

        <main className={`flex-1 px-4 pt-4 ${showTabs ? "pb-28" : "pb-10"}`}>{children}</main>

        {showTabs ? (
          <nav className="fixed inset-x-0 bottom-0 z-20 mx-auto grid h-[68px] w-full max-w-[430px] grid-cols-5 border-t border-border/80 bg-background/95 backdrop-blur-xl">
            {tabs.map(({ to, label, Icon }) => {
              const active = to === "/" ? pathname === "/" : pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={`relative flex flex-col items-center justify-center gap-1 transition-colors ${
                    active ? "text-accent" : "text-faint"
                  }`}
                >
                  {active ? <span className="absolute top-0 h-0.5 w-6 rounded-full bg-accent" /> : null}
                  <Icon className="size-[17px]" />
                  <span className="readout text-[9px]">{label}</span>
                </Link>
              );
            })}
          </nav>
        ) : null}
      </div>
    </div>
  );
}
